import React, { useEffect, useState } from 'react';
import { Alert, Box, Container, Paper, ThemeProvider, Typography } from '@mui/material';
import './TwoFactor.css';
import TwoFactorFirstTimeStep from './steps/first-time/TwoFactorFirstTimeStep';
import TwoFactorSelectMethodStep from './steps/select-method/TwoFactorSelectMethodStep';
import TwoFactorVerifyStep from './steps/verify/TwoFactorVerifyStep';
import AddTrustedDeviceStep from './steps/add-trusted-device/AddTrustedDeviceStep';
import axios from 'axios';
import qrcode from 'qrcode';
import theme from 'etl-theme';

export default function TwoFactor({
    completeUrl,
    csrfToken,
    verification,
    userEmail,
}: {
    completeUrl: string;
    csrfToken: string;
    verification: string;
    userEmail: string;
}) {
    const __ = (window as any).__ ? ((window as any).__ as (text: string) => string) : (text: string) => text;
    const [step, setStep] = useState('init');
    const [method, setMethod] = useState('');
    const [target, setTarget] = useState('');
    const [verificationId, setVerificationId] = useState('');
    const [deviceName, setDeviceName] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [qrCode, setQrCode] = useState('');
    const [otpSecret, setOtpSecret] = useState('');
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        const secret = generateSecretKey(32);
        setOtpSecret(secret);

        generateQrCode(secret).then((qrCode) => {
            setQrCode(qrCode);

            if (verification) {
                axios.get('/auth/verifications/' + verification).then((response) => {
                    setMethod(response.data.method);
                    setTarget(response.data.target);
                    setVerificationId(verification);
                    setStep('verify-and-login');
                    setLoaded(true);
                });
            } else {
                setLoaded(true);
            }
        });
    }, []);

    const generateSecretKey = (length: number = 32) => {
        const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'; // Base32 alphabet
        let secret = '';

        for (let i = 0; i < length; i++) {
            const randomIndex = Math.floor(Math.random() * charset.length);
            secret += charset[randomIndex];
        }

        return secret;
    };

    const generateQrCode = (secret: string) => {
        const generateAuthenticatorUrl = (secret: string, issuer: string) => {
            const encodedSecret = encodeURIComponent(secret);
            const encodedIssuer = encodeURIComponent(issuer);

            return `otpauth://totp/${encodedIssuer}:${userEmail}?secret=${encodedSecret}&issuer=${encodedIssuer}&algorithm=SHA1&digits=6`;
        };

        const otpUrl = generateAuthenticatorUrl(secret, 'ETL 2FA');

        return qrcode.toDataURL(otpUrl);
    };

    const goToSelectMethod = () => {
        setSubmitError('');
        setStep('select');
    };
    const goToFirstTime = () => {
        setSubmitError('');
        setStep('init');
    };
    const goToVerification = (method: string, target: string) => {
        setMethod(method);
        setTarget(target);
        setSubmitError('');

        return axios
            .post('/auth/verifications', { method: method, target: target })
            .then((result) => setVerificationId(result.data.id))
            .then(() => setStep('verify'))
            .catch(() => {
                setSubmitError(__('Wybrany adres e-mail nie jest dostępny. Podaj inny adres.'));
            });
    };
    const goToAddTrustedDevice = () => {
        setStep('add-trusted-device');
    };

    const verify = (code: string) => {
        return axios.post('/auth/verifications/' + verificationId + '/verify', { code: code }).then();
    };
    const notify = () => {
        return axios.post('/auth/verifications/' + verificationId + '/notify').catch((error) => {
            if (error?.response?.status === 429) {
                setSubmitError(__('Zbyt wiele prób. Spróbuj ponownie później.'));
            }
            throw error;
        });
    };

    const appendHidden = (form: HTMLFormElement, name: string, value: string) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    };

    const addTrustedDevice = () => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = completeUrl;

        appendHidden(form, 'verification', verificationId);
        appendHidden(form, 'deviceName', deviceName);
        if (csrfToken) {
            appendHidden(form, '__token', csrfToken);
        }

        document.body.appendChild(form);
        form.submit();
    };
    const continueWithoutAddingTrustedDevice = () => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = completeUrl;
        appendHidden(form, 'verification', verificationId);
        if (csrfToken) {
            appendHidden(form, '__token', csrfToken);
        }
        document.body.appendChild(form);
        form.submit();
    };

    return (
        <ThemeProvider theme={theme}>
            {loaded && (
                <Container maxWidth="sm">
                    <Paper sx={{ padding: 2, marginTop: 8 }}>
                        <Typography sx={{ mb: 2 }} variant="h5" gutterBottom align="center">
                            {__('Weryfikacja dwuetapowa (2FA)')}
                        </Typography>
                        {submitError && (
                            <Alert sx={{ mb: 2 }} severity="error">
                                {submitError}
                            </Alert>
                        )}
                        <Box>
                            {step == 'init' && <TwoFactorFirstTimeStep next={goToSelectMethod} />}
                            {step == 'select' && (
                                <TwoFactorSelectMethodStep
                                    otpSecret={otpSecret}
                                    next={goToVerification}
                                    back={goToFirstTime}
                                    userEmail={userEmail}
                                />
                            )}
                            {step == 'verify' && (
                                <TwoFactorVerifyStep
                                    qrCode={qrCode}
                                    target={target}
                                    method={method}
                                    next={goToAddTrustedDevice}
                                    notify={notify}
                                    back={goToSelectMethod}
                                    verify={verify}
                                    isFirstTime={true}
                                />
                            )}
                            {step == 'add-trusted-device' && (
                                <AddTrustedDeviceStep
                                    deviceName={deviceName}
                                    setDeviceName={setDeviceName}
                                    addTrustedDevice={addTrustedDevice}
                                    continueWithoutAddingTrustedDevice={continueWithoutAddingTrustedDevice}
                                />
                            )}
                            {step == 'verify-and-login' && (
                                <TwoFactorVerifyStep
                                    qrCode={null}
                                    target={target}
                                    method={method}
                                    next={goToAddTrustedDevice}
                                    notify={notify}
                                    back={null}
                                    verify={verify}
                                />
                            )}
                        </Box>
                    </Paper>
                </Container>
            )}
        </ThemeProvider>
    );
}
